'use client';

/**
 * Таблица звонков — одна на журнал и на дашборд.
 *
 * Раньше это были две разные таблицы, и они разъезжались: на дашборде не
 * было ни исхода, ни целевого, ни тегов, а «Обработка» показывала другое.
 * Человек видел один и тот же звонок двумя разными наборами полей и не мог
 * понять, какой из них правдивее. Отсюда одна разметка на оба экрана: колонку
 * теперь нельзя добавить в одном месте и забыть в другом.
 *
 * Сортировка необязательна. На дашборде её нет: там всегда десять свежих, и
 * заголовок, который выглядит кнопкой, но ничего не делает, хуже обычного
 * заголовка. `colSpan` заглушки считается от самой шапки — при добавлении
 * колонки он больше не разойдётся.
 */

import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ChevronDown, ChevronUp } from 'lucide-react';
import ScoreCell from '@/components/ui/ScoreCell';
import ProcessingCell from '@/components/ui/ProcessingCell';
import TagChip from '@/components/ui/TagChip';
import EffectiveMark from '@/components/ui/EffectiveMark';
import DispositionBadge from '@/components/ui/DispositionBadge';
import CallTypeBadge from '@/components/ui/CallTypeBadge';
import { ctMatchKind } from '@/lib/dict';
import { dateTimeCompact, duration, initials, phone } from '@/lib/format';
import type { CallSortField, CallSummary, SortDir } from '@/lib/api/types';

/** Столько колонок в шапке — на столько и растягивается заглушка. */
export const CALL_COLUMNS = 13;

export interface CallsSort {
  field: CallSortField;
  dir: SortDir;
  toggle: (field: CallSortField) => void;
}

export default function CallsTable({
  items,
  sort,
  placeholder,
}: {
  items: CallSummary[];
  /** Без него заголовки не кликабельны — так на дашборде. */
  sort?: CallsSort;
  /** Загрузка, отказ или пустой результат — одной строкой во всю ширину. */
  placeholder?: React.ReactNode;
}) {
  const router = useRouter();

  const SortIcon = ({ field }: { field: CallSortField }) => {
    if (!sort || sort.field !== field) return <ChevronUp size={12} className="text-slate-300" />;
    return sort.dir === 'asc' ? (
      <ChevronUp size={12} className="text-brand-accent" />
    ) : (
      <ChevronDown size={12} className="text-brand-accent" />
    );
  };

  const SortableHead = ({
    field,
    children,
    center = false,
  }: {
    field: CallSortField;
    children: React.ReactNode;
    center?: boolean;
  }) =>
    sort ? (
      <th className="text-left px-2.5 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">
        <button
          onClick={() => sort.toggle(field)}
          className={`flex items-center gap-1 hover:text-slate-800 transition-colors ${
            center ? 'mx-auto' : ''
          }`}
        >
          {children} <SortIcon field={field} />
        </button>
      </th>
    ) : (
      <Head center={center}>{children}</Head>
    );

  const Head = ({
    children,
    title,
    center = false,
  }: {
    children: React.ReactNode;
    title?: string;
    center?: boolean;
  }) => (
    <th
      title={title}
      className={`px-2.5 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap ${
        center ? 'text-center' : 'text-left'
      }`}
    >
      {children}
    </th>
  );

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-slate-50 border-b border-slate-100">
          <SortableHead field="startedAt">Дата и время</SortableHead>
          <Head>Сотрудник</Head>
          <Head>Кто звонил</Head>
          <Head>Кому</Head>
          <Head>Подразделение</Head>
          <Head>Тип</Head>
          <SortableHead field="talkSeconds">Разговор</SortableHead>
          <Head>Исход</Head>
          {/* Что с записью и разбором — часть обработки, а не
              отдельная колонка: три серые иконки в конце строки
              ничего не говорили, пока их не с чем было сопоставить. */}
          <Head>Обработка</Head>
          {/* Балл и звёзды одной колонкой: рядом их и смотрят —
              совпала ли арифметика правил с суждением человека.
              Сортировка остаётся по баллу, звёзды сортировке не
              поддаются и не должны. */}
          <SortableHead field="score" center>
            Балл
          </SortableHead>
          <Head title="Клиент реально говорил, а не просто дозвонился. Мера — секунды его чистой речи. Прочерк — определить не смогли, это не «нецелевой»">
            Целевой
          </Head>
          {/* Чаще пусто: рекламный источник есть только у звонков на
              номера коллтрекинга. Колонка узкая и молчит по умолчанию. */}
          <Head title="Откуда пришёл звонок: рекламный источник. Пусто — звонок на обычный номер">
            Источник
          </Head>
          <Head>Теги</Head>
        </tr>
      </thead>

      <tbody>
        {placeholder && (
          <tr>
            <td colSpan={CALL_COLUMNS} className="py-16 text-center">
              {placeholder}
            </td>
          </tr>
        )}

        {items.map((call) => (
          <tr
            key={call.id}
            onClick={() => router.push(`/call-detail?id=${call.id}`)}
            className="table-row-hover border-b border-slate-50 last:border-0 cursor-pointer"
          >
            <td className="px-2.5 py-2">
              <div className="text-xs font-mono text-slate-700 whitespace-nowrap">
                {dateTimeCompact(call.startedAt)}
              </div>
              <div className="text-[10px] text-slate-400">№{call.callSeq}</div>
            </td>

            <td className="px-2.5 py-2">
              {call.employeeName ? (
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 text-slate-600 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                    {initials(call.employeeName)}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">
                      {call.employeeName}
                    </div>
                    {call.operatorExtension && (
                      <div className="text-[10px] font-mono text-slate-400">
                        доб. {call.operatorExtension}
                      </div>
                    )}
                  </div>
                </div>
              ) : call.operatorExtension ? (
                /* Сотрудника нет, но добавочный известен: по нему
                   администратор и понимает, кого закреплять. */
                <div className="text-xs">
                  <span className="font-mono text-slate-700">{call.operatorExtension}</span>
                  <div className="text-[10px] text-amber-600">не закреплён</div>
                </div>
              ) : (
                <span
                  className="text-xs text-slate-300"
                  title="В CDR этого звонка добавочного нет — привязать его не к чему"
                >
                  —
                </span>
              )}
            </td>

            <td className="px-2.5 py-2" onClick={(event) => event.stopPropagation()}>
              {/* Пусто у внутренних: обе стороны наши, клиента нет. */}
              {call.contactId ? (
                <Link
                  href={`/contacts?id=${call.contactId}`}
                  className="text-xs font-mono text-brand-light hover:text-brand-accent hover:underline"
                  title={call.contactName ?? 'Карточка клиента'}
                >
                  {phone(call.callerNumber)}
                </Link>
              ) : (
                <span className="text-xs font-mono text-slate-700">{phone(call.callerNumber)}</span>
              )}
              {call.contactName && (
                <div className="text-[10px] text-slate-400 truncate max-w-32">
                  {call.contactName}
                </div>
              )}
              {call.isRepeatCaller && (
                <div className="text-[10px] text-slate-400" title="Снимок на момент звонка">
                  звонил раньше
                </div>
              )}
            </td>

            {/* У входящего это городской номер салона, у внутреннего —
                добавочный, у исходящего — номер клиента. */}
            <td className="px-2.5 py-2 text-xs font-mono text-slate-700">
              {phone(call.calleeNumber)}
            </td>

            <td className="px-2.5 py-2">
              <div className="text-xs text-slate-700 truncate max-w-32" title={call.unitPath}>
                {call.unitName}
              </div>
              <div className="text-[10px] text-slate-400 truncate max-w-32">{call.sourceName}</div>
            </td>

            <td className="px-2.5 py-2">
              <CallTypeBadge type={call.direction} compact />
            </td>

            {/* Ожидание подписью под разговором: колонок меньше, а
                сортировка остаётся по разговору — сортировать по
                ожиданию просили редко. */}
            <td className="px-2.5 py-2 whitespace-nowrap">
              <div className="text-xs font-mono text-slate-600">{duration(call.talkSeconds)}</div>
              {call.waitSeconds !== null && call.waitSeconds > 0 && (
                <div className="text-[10px] font-mono text-slate-400" title="Ожидание до ответа">
                  ждал {duration(call.waitSeconds)}
                </div>
              )}
            </td>

            <td className="px-2.5 py-2">
              <DispositionBadge disposition={call.disposition} size="sm" />
            </td>

            <td className="px-2.5 py-2">
              <ProcessingCell
                status={call.status}
                errorMessage={call.errorMessage}
                hasRecording={call.hasRecording}
                hasTranscript={call.hasTranscript}
                hasSummary={call.hasSummary}
              />
            </td>

            {/* Одна колонка, но два отдельных значения, а не сумма:
                балл — арифметика правил, звёзды — суждение человека,
                и среднее из них ничего не значит. */}
            <td className="px-2.5 py-2">
              <ScoreCell
                score={call.score}
                band={call.band}
                criticalViolation={call.criticalViolation}
                manualRating={call.manualRating}
                suggestedDepartment={call.suggestedDepartment}
              />
            </td>

            <td className="px-2.5 py-2">
              <EffectiveMark value={call.isEffective} compact />
            </td>

            <td className="px-2.5 py-2">
              {call.adSource ? (
                <div className="max-w-32">
                  <div className="text-xs text-slate-700 truncate">
                    {call.adSource.source || call.adSource.siteName || '—'}
                  </div>
                  {call.adSource.campaign && (
                    <div
                      className="text-[10px] text-slate-400 truncate"
                      title={call.adSource.campaign}
                    >
                      {call.adSource.campaign}
                    </div>
                  )}
                  {/* Надёжность важнее самого источника: по «повторному
                      звонку» и по уверенному совпадению решения разные. */}
                  {call.adSource.matchKind !== 'dynamic' && (
                    <div
                      className="text-[10px] text-slate-400"
                      title={ctMatchKind[call.adSource.matchKind].hint}
                    >
                      {ctMatchKind[call.adSource.matchKind].label}
                    </div>
                  )}
                </div>
              ) : (
                <span className="text-xs text-slate-300">—</span>
              )}
            </td>

            <td className="px-2.5 py-2">
              <div className="flex flex-wrap gap-1 max-w-28">
                {(call.tags ?? []).map((tag) => (
                  <TagChip key={tag.id} tag={tag} />
                ))}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
